import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../Styles/Mp4Form.css";
import { fileAPI, videosAPI } from "../../api/index"; // adjust to your actual api file path

function Mp4Form() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [videoSource, setVideoSource] = useState("youtube"); // "youtube" | "upload"
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("active");

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      setFile(selected);
      setPreview(URL.createObjectURL(selected));
      setFileName(selected.name);
    }
  };

  const handleSourceChange = (source) => {
    setVideoSource(source);
    setFile(null);
    setPreview(null);
    setFileName("");
    setYoutubeUrl("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (videoSource === "youtube" && !youtubeUrl.trim()) {
      setError("Please enter a YouTube embed URL.");
      return;
    }
    if (videoSource === "upload" && !file) {
      setError("Please choose a video file to upload.");
      return;
    }

    setSubmitting(true);
    try {
      let videoFileUrl = null;

      if (videoSource === "upload") {
        const uploadRes = await fileAPI.upload(file, "videos");
        videoFileUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url;

        if (!videoFileUrl) {
          throw new Error(
            "Upload succeeded but the response didn't contain a usable URL field."
          );
        }
      }

      await videosAPI.create({
        title,
        video_source: videoSource,
        youtube_url: videoSource === "youtube" ? youtubeUrl : null,
        video_file_url: videoSource === "upload" ? videoFileUrl : null,
        description: description || null,
        status,
      });

      navigate("/dashboard/mp4-table");
    } catch (err) {
      console.error("Video save failed:", err);
      setError(
        err.response?.data?.detail || err.message || "Something went wrong while saving the video."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Add MP4 Video</h2>
        <button className="back-btn-mp4" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      <form className="mp4-form" onSubmit={handleSubmit}>
        {error && <div className="mp4-form-error">{error}</div>}

        <div className="form-group">
          <label>Video Title</label>
          <input
            type="text"
            placeholder="Enter Video Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        {/* ---------- SOURCE TOGGLE ---------- */}
        <div className="form-group">
          <label>Video Source</label>
          <div className="mp4-source-toggle">
            <button
              type="button"
              className={`mp4-toggle-btn ${videoSource === "youtube" ? "mp4-toggle-active" : ""}`}
              onClick={() => handleSourceChange("youtube")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="20" height="14" rx="4" stroke="currentColor" strokeWidth="1.8" />
                <path d="M10 9l6 3-6 3V9z" fill="currentColor" />
              </svg>
              YouTube URL
            </button>

            <button
              type="button"
              className={`mp4-toggle-btn ${videoSource === "upload" ? "mp4-toggle-active" : ""}`}
              onClick={() => handleSourceChange("upload")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 3v12m0 0l-4.5-4.5M12 15l4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              Upload Video
            </button>
          </div>
        </div>

        {/* ---------- CONDITIONAL FIELD ---------- */}
        {videoSource === "youtube" ? (
          <div className="form-group">
            <label>YouTube Embed URL</label>
            <input
              type="url"
              placeholder="https://www.youtube.com/embed/VIDEO_ID"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              required
            />
          </div>
        ) : (
          <div className="form-group">
            <label>Upload Video File</label>
            <input type="file" accept=".mp4,video/*" onChange={handleFileChange} required />

            {fileName && (
              <div className="mp4-file-info">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                  <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                </svg>
                {fileName}
              </div>
            )}

            {preview && (
              <div className="mp4-preview-box">
                <video src={preview} controls className="mp4-preview-video" />
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            rows="4"
            placeholder="Optional Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          ></textarea>
        </div>

        <button className="sub-btn" type="submit" disabled={submitting}>
          {submitting ? "Uploading..." : "Submit MP4 Video"}
        </button>
      </form>
    </div>
  );
}

export default Mp4Form;